"use client";

import React, {
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import { useEditor, EditorContent, Editor, BubbleMenu } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Color } from "@tiptap/extension-color";
import ListItem from "@tiptap/extension-list-item";
import TextStyle from "@tiptap/extension-text-style";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";

import { Button } from "@/components/ui/button";
import {
  BoldIcon,
  CodeIcon,
  CodeXml,
  ItalicIcon,
  ListIcon,
  ListOrderedIcon,
  QuoteIcon,
  StrikethroughIcon,
  ImageIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useImageUploader } from "@/components/AppHeader/FileUpload";

import { Extension } from "@tiptap/core";
import {
  Plugin as ProseMirrorPlugin,
  PluginKey as ProseMirrorPluginKey,
} from "@tiptap/pm/state";
import { ReplaceStep } from "@tiptap/pm/transform";
import { UploadedFile } from "@/utils/types";
import { TiptapCustomLinkMark, TiptapLinkExtension } from "@/lib/tiptap";

export interface SyncedEditorProps {
  valueInSupabase: string;
  onUpdateValueInSupabase: (newValue: string) => void;
  classnames?: string;
  placeholder: string;
}

// add break after inserting image, because by default it just gets overwritten by typing anything
const AddBreakAfterImageExtension = Extension.create({
  name: "addBreakAfterImage",

  addProseMirrorPlugins() {
    return [
      new ProseMirrorPlugin({
        key: new ProseMirrorPluginKey("addBreakAfterImage"),
        appendTransaction: (transactions, oldState, newState) => {
          const tr = newState.tr;
          let modified = false;
          const hardBreakNodeType = newState.schema.nodes.hardBreak;

          if (!hardBreakNodeType) {
            return null;
          }

          transactions.forEach((transaction) => {
            if (!transaction.docChanged) return;

            transaction.steps.forEach((step) => {
              if (!(step instanceof ReplaceStep)) {
                return;
              }

              const replaceStep = step as InstanceType<typeof ReplaceStep>;
              const { from, slice } = replaceStep;

              if (slice && slice.content.size > 0) {
                slice.content.forEach((node, offsetInSlice) => {
                  if (node.type.name === "image") {
                    const imageStartPosInNewState = from + offsetInSlice;
                    const imageEndPosInNewState =
                      imageStartPosInNewState + node.nodeSize;

                    let shouldInsert = true;
                    if (imageEndPosInNewState < newState.doc.content.size) {
                      const nodeAfter = newState.doc.nodeAt(
                        imageEndPosInNewState,
                      );
                      if (nodeAfter && nodeAfter.type === hardBreakNodeType) {
                        shouldInsert = false;
                      }
                    }

                    const $imageStart = newState.doc.resolve(
                      imageStartPosInNewState,
                    );
                    const parentNode = $imageStart.parent;
                    if (
                      !parentNode
                        .contentMatchAt($imageStart.indexAfter())
                        .matchType(hardBreakNodeType)
                    ) {
                      shouldInsert = false;
                    }

                    if (shouldInsert) {
                      tr.insert(
                        imageEndPosInNewState,
                        hardBreakNodeType.create(),
                      );
                      modified = true;
                    }
                  }
                });
              }
            });
          });

          return modified ? tr : null;
        },
      }),
    ];
  },
});

export const SyncedTipTapEditor = forwardRef<any, SyncedEditorProps>(
  (props, ref) => {
    const {
      triggerUpload: triggerImageUpload,
      isUploading: isUploadingImage,
      FileUploadComponent,
      uploadFiles,
    } = useImageUploader({
      acceptedMimeTypes: [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/gif",
        "image/bmp",
        "image/tiff",
        "image/webp",
        "image/heic",
        "image/heif",
        "image/heic-sequence",
        "image/heif-sequence",
        "image/svg+xml",
      ],
      onUploadSuccess: (uploadedFile: UploadedFile) => {
        if (editor && uploadedFile.url) {
          editor.chain().focus().setImage({ src: uploadedFile.url }).run();
        }
      },
    });

    const isLocallyUpdating = useRef(false);

    const extensions = [
      Color.configure({ types: [TextStyle.name, ListItem.name] }),
      TextStyle,
      ListItem,
      TiptapCustomLinkMark,
      StarterKit.configure({
        bulletList: {
          HTMLAttributes: {
            class: "list-disc",
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: "list-decimal",
          },
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary-n-400! underline cursor-pointer",
          target: "_blank",
          rel: "noopener noreferrer",
        },
        autolink: true,
        defaultProtocol: "https",
      }),
      Placeholder.configure({
        placeholder: props.placeholder,
      }),
      Image.configure({
        inline: true,
        allowBase64: false,
      }),
      AddBreakAfterImageExtension,
      TiptapLinkExtension,
    ];

    const editor = useEditor({
      extensions,
      content: props.valueInSupabase,
      immediatelyRender: false,
      onUpdate: ({ editor }) => {
        isLocallyUpdating.current = true;
        const html = editor.getHTML();
        props.onUpdateValueInSupabase(html);
      },
      editorProps: {
        attributes: {
          tabindex: "0",
        },
        handleKeyDown: (view, event) => {
          // Prevent TipTap from handling Cmd+Enter or Ctrl+Enter
          // Let it bubble up to the global shortcut handler
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            // Return true to prevent TipTap from processing this key
            return true;
          }
          return false;
        },
        handlePaste: (view, event, slice) => {
          const files = Array.from(event.clipboardData?.files || []);
          const imageFiles = files.filter((file) =>
            file.type.startsWith("image/"),
          );

          if (imageFiles.length > 0 && uploadFiles) {
            event.preventDefault();
            uploadFiles(imageFiles);
            return true;
          }

          // Let TipTap handle all text pasting naturally
          return false;
        },
        handleDrop: (view, event, slice, moved) => {
          if (moved) {
            return false;
          }

          const files = Array.from(event.dataTransfer?.files || []);
          const imageFiles = files.filter((file) =>
            file.type.startsWith("image/"),
          );

          if (imageFiles.length > 0 && uploadFiles) {
            event.preventDefault();
            uploadFiles(imageFiles);
            return true;
          }

          return false;
        },
      },
    });

    const MenuBar = ({ editor }: { editor: Editor }) => {
      if (!editor) {
        return null;
      }

      const menuButtons = [
        {
          isActiveKey: "bold",
          text: <BoldIcon className="w-3 h-3" />,
          onClick: () => editor.chain().focus().toggleBold().run(),
          disabled: !editor.can().chain().focus().toggleBold().run(),
        },
        {
          isActiveKey: "italic",
          text: <ItalicIcon className="w-3 h-3" />,
          onClick: () => editor.chain().focus().toggleItalic().run(),
          disabled: !editor.can().chain().focus().toggleItalic().run(),
        },
        {
          isActiveKey: "strike",
          text: <StrikethroughIcon className="w-3 h-3" />,
          onClick: () => editor.chain().focus().toggleStrike().run(),
          disabled: !editor.can().chain().focus().toggleStrike().run(),
        },
        {
          isActiveKey: "code",
          text: <CodeIcon className="w-3.5 h-3.5" />,
          onClick: () => editor.chain().focus().toggleCode().run(),
          disabled: !editor.can().chain().focus().toggleCode().run(),
        },
        {
          isActive: editor.isActive("heading", { level: 1 }),
          text: "H1",
          onClick: () =>
            editor.chain().focus().toggleHeading({ level: 1 }).run(),
        },
        {
          isActive: editor.isActive("heading", { level: 2 }),
          text: "H2",
          onClick: () =>
            editor.chain().focus().toggleHeading({ level: 2 }).run(),
        },
        {
          isActive: editor.isActive("heading", { level: 3 }),
          text: "H3",
          onClick: () =>
            editor.chain().focus().toggleHeading({ level: 3 }).run(),
        },
        {
          isActiveKey: "bulletList",
          text: <ListIcon className="w-3.5 h-3.5" />,
          onClick: () => editor.chain().focus().toggleBulletList().run(),
        },
        {
          isActiveKey: "orderedList",
          text: <ListOrderedIcon className="w-3.5 h-3.5" />,
          onClick: () => editor.chain().focus().toggleOrderedList().run(),
        },
        {
          isActiveKey: "codeBlock",
          text: <CodeXml className="w-3.5 h-3.5" />,
          onClick: () => editor.chain().focus().toggleCodeBlock().run(),
        },
        {
          isActiveKey: "blockquote",
          text: <QuoteIcon className="w-3.5 h-3.5" />,
          onClick: () => editor.chain().focus().toggleBlockquote().run(),
        },
        {
          text: <ImageIcon className="w-3.5 h-3.5" />,
          onClick: triggerImageUpload,
          disabled: isUploadingImage,
        },
      ];
      return (
        <div className="flex flex-row w-full gap-0.5 p-1 bg-background-secondary rounded-md shadow-xl border">
          {menuButtons.map((button, index) => (
            <Button
              key={index}
              size="xs"
              variant={
                button.isActive ||
                (button.isActiveKey && editor.isActive(button.isActiveKey))
                  ? "primary"
                  : "ghost"
              }
              onMouseDown={(e) => {
                e.preventDefault();
                button.onClick();
              }}
              disabled={button.disabled}
              className="px-2 h-7"
            >
              {button.text}
            </Button>
          ))}
        </div>
      );
    };

    useImperativeHandle(ref, () => ({
      getText: () => editor?.getText() || "",
      focus: () => {
        editor?.commands.focus();
      },
    }));

    useEffect(() => {
      if (!editor) {
        return;
      }

      if (isLocallyUpdating.current) {
        isLocallyUpdating.current = false;
        return;
      }

      const currentHTML = editor.getHTML();
      if (currentHTML !== props.valueInSupabase) {
        editor.commands.setContent(props.valueInSupabase, false);
      }
    }, [props.valueInSupabase, editor]);

    useEffect(() => {
      if (!editor) return;

      const editorElement = editor.view.dom;
      if (!editorElement) return;

      // Set up editor for keyboard navigation
      editorElement.setAttribute("tabindex", "0");
      editor.commands.focus();

      // Handle arrow keys to prevent modal interference
      const handleKeyDown = (e: KeyboardEvent) => {
        if (
          ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)
        ) {
          e.stopPropagation();
          editorElement.focus();
        }
      };

      editorElement.addEventListener("keydown", handleKeyDown, true);

      return () => {
        editorElement.removeEventListener("keydown", handleKeyDown, true);
      };
    }, [editor]);

    if (!editor || typeof window === "undefined") {
      return null;
    }

    return (
      <div
        id="tiptap-editor"
        className={cn(
          "relative flex flex-col hide-scrollbar pb-2 shrink-0 min-h-64 px-0",
          props.classnames,
        )}
      >
        {editor && (
          <BubbleMenu editor={editor} className="z-9999 min-w-[390px]">
            <MenuBar editor={editor} />
          </BubbleMenu>
        )}
        <FileUploadComponent className="hidden" />
        <EditorContent
          editor={editor}
          autoFocus={true}
          className="focus-visible:outline-hidden flex grow w-full max-w-full overflow-x-hidden [&_a]:text-primary-n-400! [&_a]:underline [&_a]:cursor-pointer [&_a:hover]:text-primary-n-400! [&_p]:wrap-break-word [&_p]:whitespace-pre-wrap [&_p]:overflow-wrap-anywhere [&_p]:max-w-full [&_div]:wrap-break-word [&_div]:whitespace-pre-wrap [&_div]:overflow-wrap-anywhere [&_div]:max-w-full [&_span]:wrap-break-word [&_span]:whitespace-pre-wrap [&_span]:overflow-wrap-anywhere [&_span]:max-w-full [&_.ProseMirror]:wrap-break-word [&_.ProseMirror]:whitespace-pre-wrap [&_.ProseMirror]:overflow-wrap-anywhere [&_.ProseMirror]:max-w-full"
          onClick={(e) => {
            const target = e.target as HTMLElement;
            const href = target.getAttribute("href");
            if (target.tagName === "A" && href) {
              e.preventDefault();
              window.open(href, "_blank", "noopener,noreferrer");
            }
          }}
          onKeyDown={(e) => {
            if (
              ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(
                e.key,
              )
            ) {
              e.stopPropagation();
            }
          }}
        />
      </div>
    );
  },
);

export default React.memo(SyncedTipTapEditor);
